import { MouseEvent, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Button, Colors, Icon } from '@mercator-gcp/grout';
import { useNavigate } from 'react-router-dom';
import PopoverMenu from 'components/elements/PopoverMenu';
import DeleteProjectGroupModal from 'components/modals/DeleteProjectGroupModal';
import { trackGroupConfigAction } from 'services/analytics/trackGroupConfigAction';
import {
  trackImportCompanies,
  ImportCompaniesSuccessStates,
} from 'services/analytics/trackImportCompanies';
import { CompanyGroupsPageViewSections } from 'utils/analytics/constants';
import { GROUP_TYPE } from 'utils/constants';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import {
  buildDashboardURLWithParams,
  cloneSavedSearchUrl,
  savedSearchEditURL,
} from 'utils/navigation';

const triggerButtonStyle = {
  height: 'auto',
};

const deleteButtonStyle = {
  color: Colors.SystemColorNegative,
};

type TGroupPopoverMenuProps = { groupId: string | number; isCompanyGroup: boolean };

const GroupPopoverMenu = ({ groupId, isCompanyGroup }: TGroupPopoverMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const onClose = () => {
    setIsMenuOpen(false);
  };

  const onClickMenu = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDeleteSuccess = () => {
    setDeleteModalOpen(false);
    onClose();
  };

  const handleEditSearchFilters = () => {
    trackGroupConfigAction({
      group_id: String(groupId),
      group_type: isCompanyGroup ? 'company_group' : 'project_group',
      click_action: 'edit_group',
    });
    navigate(savedSearchEditURL(Number(groupId)));
    onClose();
  };

  const handleCloneGroup = () => {
    const cloneLink = cloneSavedSearchUrl(groupId);
    navigate(cloneLink);
  };

  const handleImportCompanies = () => {
    trackImportCompanies({
      group_id: String(groupId),
      section_name: CompanyGroupsPageViewSections.COMPANY_GROUPS,
      sub_section_name: String(groupId),
      click_text: 'Import Companies',
      success_state: ImportCompaniesSuccessStates.START,
    });
    const base = buildDashboardURLWithParams({
      groupId: Number(groupId),
      groupType: GROUP_TYPE.COMPANY,
    });
    navigate(`${base}&${QUERY_PARAM.importCompanies}=companies`);
    onClose();
  };

  return (
    <>
      <PopoverMenu isOpen={isMenuOpen} onClose={onClose}>
        <PopoverMenu.Trigger>
          <Box>
            <Button
              variant="unstyled"
              onClick={onClickMenu}
              sx={triggerButtonStyle}
              aria-label="group popover menu"
            >
              <Icon name={Icon.Name.KebabMenu} />
            </Button>
          </Box>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content>
          <PopoverMenu.Item
            onClick={handleEditSearchFilters}
            iconName={Icon.Name.Gear}
            label="Edit search filters"
          />
          {isCompanyGroup && (
            <PopoverMenu.Item
              onClick={handleImportCompanies}
              iconName={Icon.Name.AddToList}
              label="Import Companies"
            />
          )}
          <PopoverMenu.Item
            onClick={handleCloneGroup}
            iconName={Icon.Name.Copy}
            label="Duplicate"
          />
          <PopoverMenu.Item
            sx={deleteButtonStyle}
            onClick={() => setDeleteModalOpen(true)}
            label="Delete Group"
          />
        </PopoverMenu.Content>
      </PopoverMenu>
      {isDeleteModalOpen && (
        <DeleteProjectGroupModal
          isOpen={isDeleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onDeleteCallback={handleDeleteSuccess}
          groupId={groupId}
        />
      )}
    </>
  );
};

export default GroupPopoverMenu;
